/**
 * The contextual assistant's state: which card is up on which surface, and what the
 * shopper has already closed. One card at a time per page — the designer's frames show
 * one; several in a row would read as nagging.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { AiraContextualSurface, AiraContextualTip } from '@/features/aira/api'

const DISMISSED_KEY = 'fair:aira-contextual-dismissed'

function readDismissed(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_KEY)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set()
  }
}

function writeDismissed(ids: Set<string>) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]))
  } catch {
    /* storage may be unavailable — the dismissal then lasts for this page only */
  }
}

export const useAiraContextualStore = defineStore('aira-contextual', () => {
  const tipsBySurface = ref<Partial<Record<AiraContextualSurface, AiraContextualTip[]>>>({})
  const dismissed = ref<Set<string>>(readDismissed())
  const activeSurface = ref<AiraContextualSurface | null>(null)

  /** The page tells which surface it is; the card follows. */
  function enter(surface: AiraContextualSurface) {
    activeSurface.value = surface
  }

  function leave(surface: AiraContextualSurface) {
    if (activeSurface.value === surface) activeSurface.value = null
    tipsBySurface.value = { ...tipsBySurface.value, [surface]: [] }
  }

  /** Replace the surface's cards (from the backend or the local rules). */
  function offer(surface: AiraContextualSurface, tips: AiraContextualTip[]) {
    tipsBySurface.value = { ...tipsBySurface.value, [surface]: tips }
  }

  function dismiss(id: string) {
    const next = new Set(dismissed.value)
    next.add(id)
    dismissed.value = next
    writeDismissed(next)
  }

  /** The first card of the active surface the shopper has not closed. */
  const current = computed<AiraContextualTip | null>(() => {
    const surface = activeSurface.value
    if (!surface) return null
    return (tipsBySurface.value[surface] ?? []).find((tip) => !dismissed.value.has(tip.id)) ?? null
  })

  return { tipsBySurface, dismissed, activeSurface, current, enter, leave, offer, dismiss }
})
